import { Button } from "@/components/ui/button";
import { Camera, LogIn } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import logo from "@/assets/logo.png";

interface HeroProps {
  onTryFree: () => void;
  onSignIn: () => void;
}

const Hero = ({ onTryFree, onSignIn }: HeroProps) => {
  return (
    <section className="min-h-[90vh] flex flex-col items-center justify-center px-4 py-20 bg-gradient-to-b from-background to-secondary overflow-hidden relative">
      {/* Theme toggle in top right */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      
      <div className="max-w-4xl mx-auto text-center space-y-10 animate-fade-in-up">
        {/* Logo */}
        <div className="flex justify-center animate-scale-in">
          <img 
            src={logo} 
            alt="SnapBill.my" 
            className="h-20 md:h-24 w-auto hover:scale-110 transition-transform duration-300"
          />
        </div>

        {/* Tagline */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-foreground leading-tight">
          Snap. Bill. Paid.
        </h1>

        {/* Subtitle */}
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          The fastest way for Malaysian contractors to create professional quotes and invoices. 
          Just take a photo of your work, and let AI do the rest.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
          <Button 
            size="lg" 
            onClick={onTryFree}
            className="group w-full sm:w-auto bg-gradient-accent hover:opacity-90 text-accent-foreground shadow-lg hover:shadow-xl text-base md:text-lg px-10 py-7 rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:-translate-y-1"
          >
            <Camera className="mr-2 h-5 w-5 group-hover:rotate-12 transition-transform duration-300" />
            Try Free (No Login)
          </Button>
          <Button 
            size="lg" 
            variant="outline"
            onClick={onSignIn}
            className="w-full sm:w-auto border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground text-base md:text-lg px-10 py-7 rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-md"
          >
            <LogIn className="mr-2 h-5 w-5" />
            Sign In / Sign Up
          </Button>
        </div>

        {/* Trust Badge */}
        <div className="pt-10 flex flex-col items-center gap-3 animate-fade-in" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
          <p className="text-sm font-medium text-muted-foreground">
            Trusted by contractors across Malaysia
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-success/10 rounded-full">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
              <span>5 free tries</span>
            </div>
            <span className="hidden sm:inline">•</span>
            <div className="px-3 py-1.5 bg-primary/10 rounded-full">
              <span>No credit card required</span>
            </div>
            <span className="hidden sm:inline">•</span>
            <div className="px-3 py-1.5 bg-accent/10 rounded-full">
              <span>60-second setup</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
